/**
 * A list the partner adds to, reorders and trims (services, testimonials,
 * FAQ). Each item is a fieldset named "Service 2" and so on, with Move up,
 * Move down and Remove buttons that name the item they act on. "Add" is
 * disabled at the API's maximum and says so.
 */
import { useId, type ReactNode } from 'react';

export function Repeater<T>({
  items,
  onChange,
  max,
  noun,
  addLabel,
  make,
  disabled,
  children,
}: {
  items: T[];
  onChange: (items: T[]) => void;
  max: number;
  /** "Service", "Testimonial", "Question" — names each item. */
  noun: string;
  addLabel: string;
  make: () => T;
  disabled?: boolean;
  children: (item: T, update: (next: T) => void, index: number) => ReactNode;
}) {
  const reasonId = useId();
  const full = items.length >= max;
  const move = (from: number, to: number) => {
    const next = items.slice();
    const [it] = next.splice(from, 1);
    if (it === undefined) return;
    next.splice(to, 0, it);
    onChange(next);
  };
  return (
    <div className="stack">
      {items.map((item, i) => {
        const name = `${noun} ${i + 1}`;
        return (
          <fieldset key={i} className="sunk stack-sm" aria-label={name}>
            <div className="row-between" style={{ flexWrap: 'wrap' }}>
              <strong aria-hidden="true">{name}</strong>
              <div className="row" style={{ flexWrap: 'wrap', gap: 4 }}>
                <button type="button" className="btn btn-sm btn-ghost" disabled={disabled || i === 0} aria-label={`Move ${name.toLowerCase()} up`} onClick={() => move(i, i - 1)}>
                  Up
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  disabled={disabled || i === items.length - 1}
                  aria-label={`Move ${name.toLowerCase()} down`}
                  onClick={() => move(i, i + 1)}
                >
                  Down
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-danger-subtle"
                  disabled={disabled}
                  aria-label={`Remove ${name.toLowerCase()}`}
                  onClick={() => onChange(items.filter((_, j) => j !== i))}
                >
                  Remove
                </button>
              </div>
            </div>
            {children(item, (next) => onChange(items.map((x, j) => (j === i ? next : x))), i)}
          </fieldset>
        );
      })}
      <div className="row" style={{ flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn btn-sm"
          disabled={disabled || full}
          aria-describedby={full ? reasonId : undefined}
          onClick={() => onChange([...items, make()])}
        >
          {addLabel}
        </button>
        <span id={reasonId} className="small muted">
          {full ? `That’s the most a page can show (${max}). Remove one to add another.` : `${items.length} of ${max}`}
        </span>
      </div>
    </div>
  );
}
